import React, { useState } from 'react';
import PatientSupportFooter from './components/PatientSupportFooter';
import PageSeo from './seo/PageSeo';

const COLORS = {
  bg: '#F9FAFB',
  surface: '#FFFFFF',
  surfaceAlt: '#FAFAFA',
  primary: '#D32F2F',
  primaryDark: '#B71C1C',
  primarySoft: 'rgba(211,47,47,0.08)',
  primarySoftBorder: 'rgba(211,47,47,0.18)',
  text: '#1E1E1E',
  textSecondary: '#3A3A3A',
  textMuted: '#6B6B6B',
  border: 'rgba(0,0,0,0.08)',
  borderStrong: 'rgba(0,0,0,0.14)',
  shadowSm: '0 1px 2px rgba(0,0,0,0.04)',
  shadowMd: '0 6px 16px rgba(0,0,0,0.06)',
  shadowLg: '0 12px 28px rgba(0,0,0,0.08)',
};

const ITI_URL = 'https://validar.iti.gov.br';
const PRESCREVE_VALIDAR_URL = 'https://app.prescreve.com/validar';

const fontStack =
  "'Poppins', -apple-system, BlinkMacSystemFont, 'Inter', 'Roboto', sans-serif";

const styles = {
  page: {
    minHeight: '100vh',
    background: COLORS.bg,
    fontFamily: fontStack,
    color: COLORS.text,
    WebkitFontSmoothing: 'antialiased',
  },
  header: {
    position: 'relative',
    background: COLORS.surface,
    borderBottom: `1px solid ${COLORS.border}`,
    padding: 'max(40px, env(safe-area-inset-top, 0px)) 24px 56px',
    textAlign: 'center',
    overflow: 'hidden',
  },
  headerAccent: {
    position: 'absolute',
    inset: 0,
    background:
      'radial-gradient(ellipse 60% 80% at 50% 0%, rgba(211,47,47,0.07) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  headerInner: {
    position: 'relative',
    maxWidth: 720,
    margin: '0 auto',
  },
  headerTitle: {
    fontSize: 'clamp(24px, 5.2vw, 36px)',
    fontWeight: 700,
    margin: '0 0 14px',
    lineHeight: 1.2,
    color: COLORS.text,
    letterSpacing: '-0.01em',
  },
  headerSub: {
    fontSize: 'clamp(15px, 2.6vw, 17px)',
    color: COLORS.textMuted,
    maxWidth: 600,
    margin: '0 auto',
    lineHeight: 1.6,
  },
  container: {
    maxWidth: 760,
    margin: '0 auto',
    padding: '32px 20px 64px',
  },
  topAlert: {
    display: 'flex',
    gap: 16,
    alignItems: 'flex-start',
    padding: '20px 22px',
    background: COLORS.surface,
    border: `1px solid ${COLORS.primarySoftBorder}`,
    borderLeft: `4px solid ${COLORS.primary}`,
    borderRadius: 14,
    marginBottom: 28,
    boxShadow: COLORS.shadowSm,
  },
  topAlertBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: 10,
    background: COLORS.primarySoft,
    color: COLORS.primary,
    flexShrink: 0,
  },
  topAlertTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: COLORS.text,
    margin: '0 0 4px',
    lineHeight: 1.4,
  },
  topAlertText: {
    fontSize: 14,
    lineHeight: 1.6,
    color: COLORS.textSecondary,
    margin: 0,
  },
  outlookAlert: {
    display: 'flex',
    gap: 16,
    alignItems: 'flex-start',
    padding: '20px 22px',
    background: '#FEF2F2',
    border: '1px solid #FCA5A5',
    borderLeft: '4px solid #DC2626',
    borderRadius: 14,
    marginBottom: 16,
    boxShadow: COLORS.shadowSm,
  },
  outlookAlertBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: 10,
    background: '#DC2626',
    color: '#FFFFFF',
    fontSize: 22,
    fontWeight: 800,
    flexShrink: 0,
    lineHeight: 1,
  },
  outlookAlertTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#7F1D1D',
    margin: '0 0 4px',
    lineHeight: 1.4,
  },
  outlookAlertText: {
    fontSize: 14,
    lineHeight: 1.6,
    color: '#991B1B',
    margin: '4px 0 0',
  },
  outlookAlertLink: {
    color: '#7C2D12',
    fontWeight: 600,
    textDecoration: 'underline',
  },
  pdfOriginalAlert: {
    display: 'flex',
    gap: 16,
    alignItems: 'flex-start',
    padding: '20px 22px',
    background: '#FFFBEB',
    border: '1px solid #FCD34D',
    borderLeft: '4px solid #D97706',
    borderRadius: 14,
    marginBottom: 16,
    boxShadow: COLORS.shadowSm,
  },
  pdfOriginalAlertBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: 10,
    background: '#D97706',
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: 800,
    flexShrink: 0,
    lineHeight: 1.1,
    textAlign: 'center',
    letterSpacing: '0.02em',
  },
  pdfOriginalAlertTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#78350F',
    margin: '0 0 4px',
    lineHeight: 1.4,
  },
  pdfOriginalAlertText: {
    fontSize: 14,
    lineHeight: 1.6,
    color: '#92400E',
    margin: '4px 0 0',
  },
  section: {
    background: COLORS.surface,
    borderRadius: 16,
    padding: 'clamp(24px, 4vw, 32px)',
    marginBottom: 20,
    border: `1px solid ${COLORS.border}`,
    boxShadow: COLORS.shadowSm,
  },
  sectionTitle: {
    fontSize: 'clamp(18px, 3.4vw, 22px)',
    fontWeight: 700,
    margin: '0 0 6px',
    color: COLORS.text,
    letterSpacing: '-0.01em',
  },
  sectionEyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '1.2px',
    textTransform: 'uppercase',
    color: COLORS.primary,
    margin: '0 0 8px',
  },
  paragraph: {
    margin: '0 0 12px',
    fontSize: 15,
    lineHeight: 1.7,
    color: COLORS.textSecondary,
  },
  divider: {
    height: 1,
    background: COLORS.border,
    margin: '20px 0',
    border: 0,
  },
  step: {
    display: 'flex',
    gap: 16,
    padding: '14px 4px',
    alignItems: 'flex-start',
    borderTop: `1px solid ${COLORS.border}`,
  },
  stepFirst: {
    borderTop: 'none',
  },
  stepNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: COLORS.text,
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
    marginTop: 2,
    letterSpacing: '0.3px',
  },
  stepContent: {
    flex: 1,
    fontSize: 15,
    lineHeight: 1.65,
    color: COLORS.textSecondary,
    paddingTop: 4,
  },
  link: {
    color: COLORS.primary,
    fontWeight: 600,
    textDecoration: 'none',
    borderBottom: `1px solid ${COLORS.primarySoftBorder}`,
    transition: 'border-color 0.15s, color 0.15s',
  },
  faqList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  faqItem: {
    borderTop: `1px solid ${COLORS.border}`,
  },
  faqItemFirst: {
    borderTop: 'none',
  },
  faqButton: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    padding: '18px 4px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 600,
    color: COLORS.text,
    textAlign: 'left',
    fontFamily: 'inherit',
    transition: 'color 0.15s',
  },
  faqChevron: {
    flexShrink: 0,
    transition: 'transform 0.25s ease',
    color: COLORS.textMuted,
  },
  faqAnswer: {
    padding: '0 4px 18px',
    fontSize: 14.5,
    lineHeight: 1.7,
    color: COLORS.textSecondary,
    animation: 'faqOpen 0.2s ease-out',
  },
  helpBox: {
    background: COLORS.text,
    color: '#fff',
    borderRadius: 18,
    padding: 'clamp(24px, 4vw, 36px)',
    marginBottom: 24,
    boxShadow: COLORS.shadowLg,
    position: 'relative',
    overflow: 'hidden',
  },
  helpBoxAccent: {
    position: 'absolute',
    top: -60,
    right: -60,
    width: 200,
    height: 200,
    borderRadius: '50%',
    background:
      'radial-gradient(circle, rgba(211,47,47,0.45) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  helpInner: {
    position: 'relative',
  },
  helpEyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '1.4px',
    textTransform: 'uppercase',
    color: '#FFB4B4',
    margin: '0 0 10px',
  },
  helpTitle: {
    fontSize: 'clamp(20px, 3.6vw, 24px)',
    fontWeight: 700,
    margin: '0 0 12px',
    color: '#fff',
    letterSpacing: '-0.01em',
  },
  helpText: {
    margin: '0 0 20px',
    fontSize: 15,
    lineHeight: 1.65,
    color: 'rgba(255,255,255,0.78)',
  },
  emailCard: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 12,
    padding: '18px 20px',
    marginBottom: 22,
  },
  emailLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '1.2px',
    color: 'rgba(255,255,255,0.55)',
    margin: '0 0 6px',
  },
  emailValue: {
    fontSize: 'clamp(15px, 4.2vw, 18px)',
    fontWeight: 600,
    color: '#fff',
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontFamily: 'inherit',
    letterSpacing: '-0.01em',
  },
  checklist: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  checkItem: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    fontSize: 14,
    lineHeight: 1.6,
    color: 'rgba(255,255,255,0.88)',
  },
  checkBullet: {
    flexShrink: 0,
    width: 20,
    height: 20,
    borderRadius: 6,
    background: COLORS.primary,
    color: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  },
  fastcheckBanner: {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: 20,
    alignItems: 'center',
    padding: '18px 22px',
    borderRadius: 16,
    background: `linear-gradient(135deg, ${COLORS.text} 0%, #2A2A2A 100%)`,
    color: '#fff',
    marginBottom: 24,
    boxShadow: COLORS.shadowLg,
    overflow: 'hidden',
  },
  fastcheckBannerText: {
    minWidth: 0,
  },
  fastcheckBannerEyebrow: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '1.4px',
    textTransform: 'uppercase',
    color: '#FFB4B4',
    margin: '0 0 6px',
  },
  fastcheckBannerTitle: {
    fontSize: 'clamp(15px, 3.2vw, 17px)',
    fontWeight: 700,
    margin: '0 0 4px',
    color: '#fff',
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  fastcheckBannerSub: {
    fontSize: 13,
    lineHeight: 1.55,
    color: 'rgba(255,255,255,0.72)',
    margin: 0,
  },
  fastcheckBannerCta: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '10px 16px',
    borderRadius: 10,
    background: COLORS.primary,
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    transition: 'background 0.15s, transform 0.08s',
  },
  fastcheckBannerAccent: {
    position: 'absolute',
    top: -80,
    right: -60,
    width: 180,
    height: 180,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(211,47,47,0.35) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  footer: {
    textAlign: 'center',
    padding: '24px 20px 48px',
    color: COLORS.textMuted,
    fontSize: 13,
    lineHeight: 1.6,
  },
  footerLink: {
    color: COLORS.text,
    fontWeight: 600,
    textDecoration: 'none',
    borderBottom: `1px solid ${COLORS.borderStrong}`,
    transition: 'color 0.15s, border-color 0.15s',
  },
};

function ChevronIcon({ open }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        ...styles.faqChevron,
        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
      }}
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function ShieldAlertIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ArrowLeftIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ verticalAlign: '-2px', marginRight: 6 }}
      aria-hidden="true"
    >
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

function FAQ({ question, children, isFirst }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  return (
    <div style={{ ...styles.faqItem, ...(isFirst ? styles.faqItemFirst : {}) }}>
      <button
        type="button"
        style={{
          ...styles.faqButton,
          color: hover || open ? COLORS.primary : COLORS.text,
        }}
        onClick={() => setOpen(!open)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        aria-expanded={open}
      >
        <span>{question}</span>
        <ChevronIcon open={open} />
      </button>
      {open && <div style={styles.faqAnswer}>{children}</div>}
    </div>
  );
}

function Step({ n, isFirst, children }) {
  return (
    <div style={{ ...styles.step, ...(isFirst ? styles.stepFirst : {}) }}>
      <span style={styles.stepNumber}>{n}</span>
      <div style={styles.stepContent}>{children}</div>
    </div>
  );
}

const Autenticar = () => (
  <div style={styles.page}>
    <PageSeo
      path="/autenticar"
      title="Autenticar atestado ou receita digital — Consulta+"
      description="Verifique a autenticidade de atestados, receitas e pedidos de exame emitidos pela Consulta+. Documentos assinados digitalmente com certificado ICP-Brasil, válidos em todo o Brasil."
      breadcrumbs={[
        { name: 'Início', url: '/' },
        { name: 'Autenticar documento', url: '/autenticar' },
      ]}
    />
    <style>{`
      @keyframes faqOpen {
        from { opacity: 0; transform: translateY(-4px); }
        to { opacity: 1; transform: translateY(0); }
      }
      a.autenticar-link:hover {
        border-bottom-color: ${COLORS.primary} !important;
      }
      a.autenticar-footer-link:hover {
        color: ${COLORS.primary} !important;
        border-bottom-color: ${COLORS.primary} !important;
      }
      a.autenticar-easycheck-cta:hover {
        background: ${COLORS.primaryDark} !important;
        transform: translateY(-1px);
      }
      @media (max-width: 520px) {
        aside[aria-label="Serviço Easycheck para empresas"] {
          grid-template-columns: 1fr !important;
        }
        aside[aria-label="Serviço Easycheck para empresas"] a.autenticar-easycheck-cta {
          justify-self: stretch;
          text-align: center;
        }
      }
    `}</style>

    <header style={styles.header}>
      <div style={styles.headerAccent} aria-hidden="true" />
      <div style={styles.headerInner}>
        <h1 style={styles.headerTitle}>
          Como autenticar seu atestado digital
        </h1>
        <p style={styles.headerSub}>
          Passo a passo para pacientes e empresas validarem a assinatura digital
          do atestado emitido pela Consulta+ no portal oficial do Governo
          Federal (ITI).
        </p>
        <img
          src="/img/heros/hero-autenticar-16x9.webp"
          alt="Pintura modernista de uma lupa sobre um documento com selo dourado e um escudo com selo de verificação"
          loading="lazy"
          style={{
            width: '100%',
            maxWidth: 640,
            aspectRatio: '16 / 7',
            objectFit: 'cover',
            borderRadius: 16,
            boxShadow: '0 6px 18px rgba(15,23,42,0.12)',
            display: 'block',
            margin: '18px auto 0',
          }}
        />
      </div>
    </header>

    <div style={styles.container}>
      {/* Easycheck temporariamente fora do ar (18/08/2026). Religar: descomentar.
      <aside style={styles.fastcheckBanner} aria-label="Serviço Easycheck para empresas">
        <div style={styles.fastcheckBannerAccent} aria-hidden="true" />
        <div style={styles.fastcheckBannerText}>
          <p style={styles.fastcheckBannerEyebrow}>Para empresas / RH</p>
          <p style={styles.fastcheckBannerTitle}>
            Easycheck: verificação fácil
          </p>
          <p style={styles.fastcheckBannerSub}>
            Alternativa opcional ao tutorial gratuito abaixo: R$&nbsp;3,99 por
            documento, com relatório formal da Consulta+.
          </p>
        </div>
        <a
          href="/easycheck"
          style={styles.fastcheckBannerCta}
          className="autenticar-easycheck-cta"
        >
          Ir para Easycheck →
        </a>
      </aside>
      */}

      {/* Validador do ITI instável em documento médico desde 21/10/2026. Tirar quando o ITI voltar a validar
          (junto com ATIVO em functions/lib/supportSofiaItiInstabilidade.js). */}
      <div style={styles.outlookAlert} role="alert">
        <span style={styles.outlookAlertBadge} aria-hidden="true">!</span>
        <div>
          <p style={styles.outlookAlertTitle}>
            Instabilidade no validador do ITI desde 21/10/2026
          </p>
          <p style={styles.outlookAlertText}>
            O site <strong>validar.iti.gov.br</strong> está exibindo a mensagem{' '}
            <strong>“Ocorreu um erro na validação do seu arquivo”</strong> para
            atestados e receitas assinados por médico,{' '}
            <strong>mesmo quando o arquivo é o PDF original</strong>. A falha é
            do validador do ITI e <strong>não indica documento falso</strong>{' '}
            nem alterado.
          </p>
          <p style={styles.outlookAlertText}>
            <strong>Enquanto isso:</strong> acesse{' '}
            <a
              href={PRESCREVE_VALIDAR_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.outlookAlertLink}
            >
              app.prescreve.com/validar
            </a>{' '}
            e informe o <strong>Token</strong> impresso no rodapé do documento.
            A página mostra a via digital emitida, para comparação com o arquivo
            recebido. Quando o ITI normalizar, a assinatura volta a ser validada
            pelo passo a passo abaixo.
          </p>
        </div>
      </div>

      <div style={styles.outlookAlert} role="alert">
        <span style={styles.outlookAlertBadge} aria-hidden="true">!</span>
        <div>
          <p style={styles.outlookAlertTitle}>
            Atestado dando &quot;inválido&quot;? Cuidado com o app Outlook do iPhone
          </p>
          <p style={styles.outlookAlertText}>
            O app <strong>Outlook do iPhone</strong> converte automaticamente o PDF
            do atestado em <strong>imagem</strong> ao baixar o anexo, o que{' '}
            <strong>invalida a assinatura digital ICP-Brasil</strong>. O mesmo
            acontece se você imprimir e digitalizar o PDF de volta.
          </p>
          <p style={styles.outlookAlertText}>
            <strong>Solução:</strong> abra o email no <strong>Safari</strong>,{' '}
            <strong>Chrome</strong> ou no <strong>Gmail pelo navegador</strong> e
            baixe o PDF original.
          </p>
        </div>
      </div>

      <div style={styles.pdfOriginalAlert} role="alert">
        <span style={styles.pdfOriginalAlertBadge} aria-hidden="true">
          ATENÇÃO!
        </span>
        <div>
          <p style={styles.pdfOriginalAlertTitle}>
            Erro na validação? Use sempre o PDF original
          </p>
          <p style={styles.pdfOriginalAlertText}>
            Casos frequentes em <strong>pacientes e empresas</strong>: imprimir
            e escanear o documento, converter para imagem (JPG/PNG), ou{' '}
            <strong>alterar o nome ou o tipo do arquivo</strong> — isso remove a
            assinatura digital ICP-Brasil e a validação no ITI falha.
          </p>
          <p style={styles.pdfOriginalAlertText}>
            <strong>Dica:</strong> certifique-se de usar o{' '}
            <strong>PDF original</strong> enviado pela Consulta+. Se o arquivo
            for impresso e escaneado, ou convertido para imagem, a assinatura
            digital será perdida e a validação falhará.
          </p>
          <p style={styles.pdfOriginalAlertText}>
            A mensagem <strong>“Ocorreu um erro na validação do seu arquivo”</strong>,
            exibida desde 21/10/2026, aparece também com o PDF original. Nesse
            caso, siga o primeiro aviso desta página.
          </p>
        </div>
      </div>

      <div style={styles.topAlert} role="alert">
        <span style={styles.topAlertBadge} aria-hidden="true">
          <ShieldAlertIcon />
        </span>
        <div>
          <p style={styles.topAlertTitle}>
            Atenção: problemas na validação via QR Code
          </p>
          <p style={styles.topAlertText}>
            Temos identificado <strong>falhas intermitentes</strong> na
            verificação por QR Code no portal do ITI.{' '}
            <strong>Faça a autenticação pelo upload do arquivo PDF</strong> — é
            o método oficial, mais estável e confiável.
          </p>
        </div>
      </div>

      <section style={styles.section}>
        <p style={styles.sectionEyebrow}>O que é</p>
        <h2 style={styles.sectionTitle}>Validação ITI</h2>
        <hr style={styles.divider} />
        <p style={styles.paragraph}>
          O <strong>Instituto Nacional de Tecnologia da Informação (ITI)</strong>{' '}
          é o órgão do Governo Federal responsável pela Infraestrutura de
          Chaves Públicas Brasileira (ICP-Brasil). Todo atestado digital
          emitido pela Consulta+ possui{' '}
          <strong>assinatura digital com certificado ICP-Brasil</strong> e pode
          ser verificado no portal oficial do ITI.
        </p>
        <p style={{ ...styles.paragraph, marginBottom: 0 }}>
          Ao validar, o sistema confirma que o documento é autêntico, foi
          assinado por um profissional habilitado e que{' '}
          <strong>não foi alterado</strong> desde a emissão.
        </p>
      </section>

      <section style={styles.section}>
        <p style={styles.sectionEyebrow}>Passo a passo</p>
        <h2 style={styles.sectionTitle}>Validação via upload do PDF</h2>
        <p style={{ ...styles.paragraph, marginTop: 8 }}>
          Este é o método <strong>oficial e recomendado</strong> para
          pacientes, empresas e departamentos de RH validarem o atestado
          digital.
        </p>
        <hr style={styles.divider} />

        <Step n={1} isFirst>
          <strong>Baixe o atestado em PDF.</strong> O paciente recebe o arquivo
          PDF do atestado diretamente no app ou por e-mail ao final da
          consulta. Caso seja empresa, solicite ao colaborador o envio do
          arquivo PDF original.
        </Step>

        <Step n={2}>
          Acesse o site oficial do ITI:{' '}
          <a
            href={ITI_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.link}
            className="autenticar-link"
          >
            validar.iti.gov.br
          </a>
        </Step>

        <Step n={3}>
          Na página inicial, clique no botão{' '}
          <strong>“Escolher Arquivo”</strong> (ou arraste o PDF para a área
          indicada).
        </Step>

        <Step n={4}>
          Selecione o <strong>arquivo PDF do atestado</strong> que foi salvo no
          seu computador ou celular.
        </Step>

        <Step n={5}>
          Clique em <strong>“Verificar conformidade”</strong> e aguarde a
          análise.
        </Step>

        <Step n={6}>
          O resultado será exibido na tela. Se a assinatura estiver válida, o
          sistema mostrará os <strong>dados do assinante</strong> (médico
          responsável), o <strong>status da assinatura</strong> e a{' '}
          <strong>data/hora da assinatura</strong>.
        </Step>
      </section>

      <section style={styles.section}>
        <p style={styles.sectionEyebrow}>Dúvidas comuns</p>
        <h2 style={styles.sectionTitle}>Perguntas frequentes</h2>
        <hr style={styles.divider} />

        <div style={styles.faqList}>
          <FAQ question="O atestado digital tem validade jurídica?" isFirst>
            <p style={{ margin: 0 }}>
              Sim. Os atestados emitidos pela Consulta+ possuem assinatura
              digital com certificado ICP-Brasil, conforme a Medida Provisória
              2.200-2/2001, tendo a mesma validade jurídica de um documento
              assinado em cartório.
            </p>
          </FAQ>

          <FAQ question="A empresa pode recusar um atestado digital?">
            <p style={{ margin: 0 }}>
              Não. A legislação brasileira (Lei 14.063/2020 e MP 2.200-2/2001)
              garante que documentos com assinatura digital ICP-Brasil têm
              validade legal plena. A empresa deve aceitar o atestado digital
              da mesma forma que aceitaria um atestado em papel.
            </p>
          </FAQ>

          <FAQ question="Preciso imprimir o atestado?">
            <p style={{ margin: 0 }}>
              Não é necessário. O atestado digital em PDF já é o documento
              oficial. Se preferir entregar uma cópia impressa, a empresa ainda
              deve validar a versão digital (PDF original) para confirmar a
              autenticidade.
            </p>
          </FAQ>

          <FAQ question="Posso validar pelo QR Code do atestado?">
            <p style={{ margin: 0 }}>
              No momento, <strong>não recomendamos</strong> a validação via QR
              Code. Temos identificado falhas intermitentes no portal do ITI
              que podem impedir a verificação. Utilize sempre o{' '}
              <strong>upload do arquivo PDF</strong> no site{' '}
              <a
                href={ITI_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.link}
                className="autenticar-link"
              >
                validar.iti.gov.br
              </a>
              . É o método oficial, estável e com o mesmo resultado.
            </p>
          </FAQ>
        </div>
      </section>

      {/* Easycheck temporariamente fora do ar (18/08/2026). Religar: descomentar.
      <div style={styles.helpBox}>
        <div style={styles.helpBoxAccent} aria-hidden="true" />
        <div style={styles.helpInner}>
          <p style={styles.helpEyebrow}>Para empresas / RH</p>
          <h2 style={styles.helpTitle}>Verificação Fácil</h2>
          <p style={styles.helpText}>
            O Easycheck é o serviço opcional da própria plataforma: você envia
            o PDF do atestado, paga R$&nbsp;3,99 via Pix e recebe o relatório
            formal na tela e por e-mail, com NFS-e automática no CNPJ. Resultado
            na maioria das vezes instantâneo; se precisar de revisão humana, em
            até 12 horas. O caminho gratuito do ITI acima continua válido e
            suficiente.
          </p>

          <ul style={styles.checklist}>
            <li style={styles.checkItem}>
              <span style={styles.checkBullet} aria-hidden="true">
                <CheckIcon />
              </span>
              <span>
                Relatório formal emitido pela Consulta+, sem revelar dados
                clínicos do colaborador.
              </span>
            </li>
            <li style={styles.checkItem}>
              <span style={styles.checkBullet} aria-hidden="true">
                <CheckIcon />
              </span>
              <span>
                Resultado na maioria das vezes instantâneo; se precisar de
                revisão humana, em até 12 horas.
              </span>
            </li>
            <li style={styles.checkItem}>
              <span style={styles.checkBullet} aria-hidden="true">
                <CheckIcon />
              </span>
              <span>
                R$&nbsp;3,99 por documento, com nota fiscal para o CNPJ da
                empresa.
              </span>
            </li>
          </ul>

          <a
            href="/easycheck"
            style={{
              ...styles.fastcheckBannerCta,
              display: 'inline-block',
              marginTop: 22,
              textDecoration: 'none',
            }}
            className="autenticar-easycheck-cta"
          >
            Ir para Easycheck →
          </a>
        </div>
      </div>
      */}

      <footer style={styles.footer}>
        <p style={{ margin: '0 0 10px' }}>
          <a
            href="/"
            style={styles.footerLink}
            className="autenticar-footer-link"
          >
            <ArrowLeftIcon />
            Voltar para consultamais.app
          </a>
        </p>
        <p style={{ margin: 0 }}>
          Consulta+ — Teleconsulta médica com receita e atestado digital.
        </p>
      </footer>
      <PatientSupportFooter />
    </div>
  </div>
);

export default Autenticar;
